import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef
} from 'react';
import {
  TextField as MuiTextfield,
  TextFieldProps,
  InputAdornment,
  IconButton
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import deepmerge from '@mui/utils/deepmerge';
import styles from './styles';

export interface LibTextfieldProps {
  label?: string;
  name?: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'tel' | 'date';
  readOnly?: boolean;
  isSuccess?: boolean;
  hasNumberStepper?: boolean;
  message?: string | undefined;
  startAdornmentOption?: boolean;
  endAdornmentOption?: boolean;
  startInputAdornment?: React.ReactNode;
  endInputAdornment?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  regex?: RegExp;
  helperText?: string | undefined | false;
  fieldType?: string | undefined;
  allowSpecialCharacter?: boolean;
  hasInputPropHandleChange?: boolean;
  disabled?: boolean;
}

const LibTextfield = forwardRef(
  (
    {
      type = 'text',
      variant,
      readOnly,
      isSuccess,
      startAdornmentOption,
      endAdornmentOption,
      startInputAdornment,
      endInputAdornment,
      hasNumberStepper = true,
      onChange,
      onBlur,
      regex,
      error,
      helperText,
      fieldType,
      allowSpecialCharacter,
      hasInputPropHandleChange,
      disabled,
      ...props
    }: LibTextfieldProps & Partial<TextFieldProps>,
    ref: React.ForwardedRef<any>
  ) => {
    const paramsref = useRef(null);
    const [value, setValue] = useState('');
    const [visiblePass, setVisiblePass] = React.useState(false);
    const [specialCharactersError, setSpecialCharactersError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(
      'Special characters are not allowed'
    );

    const handleClickShowPassword = () => {
      setVisiblePass(!visiblePass);
    };

    const getEndAdornamentIcon = (
      textfieldType: 'text' | 'number' | 'email' | 'password' | 'tel' | 'date',
      isEndAdornmentOption: boolean,
      isEndInputAdornment: React.ReactNode
    ) => {
      if (textfieldType === 'password' && isEndAdornmentOption) {
        return (
          <InputAdornment position="end">
            {isEndInputAdornment || (
              <IconButton
                onClick={handleClickShowPassword}
                edge="end"
                aria-label="toggle password visibility"
              >
                {visiblePass ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            )}
          </InputAdornment>
        );
      }

      if (isEndAdornmentOption) {
        return (
          <InputAdornment position="end">
            {isEndInputAdornment || null}
          </InputAdornment>
        );
      }

      return null;
    };

    const containsSpecialChar = (valueStr: string): string => {
      let specialCharReg = /[~`#$%^&*+=\\[\]\\;/{}|\\":<>()_]/g;

      if (fieldType === 'email') {
        specialCharReg = /(.*?)/;
      }

      if (fieldType === 'phone') {
        specialCharReg = /[~`!@#$%^&*=\\[\]\\;/{}|\\":<>_]/g;
        setErrorMessage('Special characters are not allowed except +');
      }

      if (fieldType === 'postCode') {
        specialCharReg = /[~`@#!$%^&*=[\]\\/{}|":<>()_,'.;-]/g;
        setErrorMessage('Special characters are not allowed');
      }

      if (fieldType === 'name') {
        specialCharReg = /[~`@#!$%^*+=\\[\]\\;/{}|\\":<>()_]/g;
        setErrorMessage('Special characters are not allowed except - &');
      }

      if (fieldType === 'password') {
        specialCharReg = /(.*?)/;
      }

      if (regex) {
        specialCharReg = new RegExp(regex, 'g');
      }

      return specialCharReg.test(valueStr)
        ? valueStr.replace(specialCharReg, '')
        : valueStr;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      const val = e?.target?.value;

      setValue(val);

      if (!allowSpecialCharacter) {
        e.target.value = containsSpecialChar(val);
      } else {
        setSpecialCharactersError(false);
      }

      if (hasInputPropHandleChange) {
        props?.InputProps?.onChange?.(e);
      } else {
        onChange?.(e);
      }
    };

    useImperativeHandle(ref, () => ({
      getValue: () => value,
      focus: () => paramsref.current?.focus()
    }));

    const extractInputRef = (instance) => {
      paramsref.current = instance;
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      e.preventDefault();
      setSpecialCharactersError(false);
      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === 'number') {
        if (e.key === 'e' || e.key === 'E') {
          e.preventDefault();
        }
      }
    };

    return (
      <MuiTextfield
        type={type === 'password' && visiblePass ? 'text' : type}
        error={specialCharactersError || error}
        helperText={
          !helperText && specialCharactersError ? errorMessage : helperText
        }
        onBlur={handleBlur}
        variant={variant || (readOnly ? 'standard' : 'outlined')}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        InputLabelProps={readOnly ? { shrink: true } : { shrink: false }}
        sx={deepmerge(
          deepmerge(styles.base, !hasNumberStepper && styles.hideNumberStepper),
          props.sx && props.sx
        )}
        inputRef={extractInputRef}
        {...props}
        InputProps={{
          startAdornment: startAdornmentOption ? (
            <InputAdornment position="start">
              {startInputAdornment || <AccountCircle />}
            </InputAdornment>
          ) : null,
          endAdornment: getEndAdornamentIcon(
            type,
            endAdornmentOption,
            endInputAdornment
          ),
          readOnly,
          sx: isSuccess ? { ...styles.isSuccess } : null,
          ...props.InputProps,
          onChange: handleChange
        }}
      />
    );
  }
);

LibTextfield.displayName = 'LibTextfield';

export const Textfield = React.memo(LibTextfield);

export default Textfield;
